import axios from 'axios';

// The access token keeps the old "token" key, so websockets and ?auth= URLs keep reading it unchanged
export const TOKEN = "token";
export const REFRESH_TOKEN = "refreshToken";
export const TOKEN_EXPIRES_AT = "accessTokenExpiresAt";

// Refresh this long before the access token expires (it lives ~15 minutes, the session up to 10 days)
const REFRESH_MARGIN_MS = 30_000;

export const getAccessToken = () => localStorage.getItem(TOKEN);

// Refresh returns a new refresh token too; the stored one is always replaced
export const saveTokens = ({accessToken, refreshToken, expiresIn}) => {
  localStorage.setItem(TOKEN, accessToken);
  if (refreshToken) localStorage.setItem(REFRESH_TOKEN, refreshToken);
  localStorage.setItem(TOKEN_EXPIRES_AT, String(Date.now() + (expiresIn || 900) * 1000));
};

export const clearTokens = () => {
  localStorage.removeItem(TOKEN);
  localStorage.removeItem(REFRESH_TOKEN);
  localStorage.removeItem(TOKEN_EXPIRES_AT);
};

export const logoutLocally = () => {
  clearTokens();
  if (window.location.pathname !== '/login') window.location.href = '/login';
};

// The session is over only when /api/auth/refresh itself rejects the refresh token: 401 INVALID_CREDENTIALS
// (logged out or the 10-day session expired) or 404 ACCOUNT_DISABLED. A network or server error is not a
// reason to log out; the next request tries again.
export const isSessionEnded = (error) => [401, 404].includes(error?.response?.status);

let refreshing = null;

// One refresh at a time, even when many requests need it together.
// Plain axios, so the refresh call does not go through the interceptors.
export const refreshTokens = () => {
  if (!refreshing) {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN);
    refreshing = (refreshToken
      ? axios.post(
        `${process.env.REACT_APP_USERMANAGEMENT_GATEWAY}/api/auth/refresh`,
        {refreshToken},
        {headers: {"Content-Type": "application/json"}}
      )
      // without a refresh token (e.g. stored before the move to Keycloak) the session cannot continue
      : Promise.reject({response: {status: 401}}))
      .then(response => saveTokens(response.data))
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
};

// Refreshes, logging out when the session has ended. Resolves to whether there is a fresh access token.
const refreshOrLogout = async () => {
  try {
    await refreshTokens();
    return true;
  } catch (error) {
    if (isSessionEnded(error)) logoutLocally();
    else console.error("Failed to refresh the session", error);
    return false;
  }
};

// Returns an access token, refreshing it first when it is about to expire
export const getValidAccessToken = async () => {
  if (!getAccessToken()) return null;
  const expiresAt = Number(localStorage.getItem(TOKEN_EXPIRES_AT) ?? 0);
  if (Date.now() > expiresAt - REFRESH_MARGIN_MS) await refreshOrLogout();
  return getAccessToken();
};

// STOMP beforeConnect: every connect and reconnect of a websocket sends a current access token, so a socket
// that reconnects after the token expired is not rejected
export const refreshConnectHeaders = async (client) => {
  const token = await getValidAccessToken();
  client.connectHeaders = token ? {Authorization: `Bearer ${token}`} : {};
};

// Sends the access token on every request of an axios instance: refreshed shortly before it expires, and on a
// 401 refreshed once and the request retried. skip(config): requests that handle their own 401s (login).
export const withAuth = (instance, {skip = () => false} = {}) => {
  instance.interceptors.request.use(async (config) => {
    const token = skip(config) ? null : await getValidAccessToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error.config;
      // a 401 right after a refresh is not an expired token, so it is not retried again
      if (error.response?.status !== 401 || !config || config._retried || skip(config) || !getAccessToken()) {
        return Promise.reject(error);
      }
      if (!(await refreshOrLogout())) return Promise.reject(error);
      config._retried = true;
      config.headers.Authorization = `Bearer ${getAccessToken()}`;
      return instance(config);
    }
  );
  return instance;
};

// For the MCP registry / Knowledge Manager: a 401 logs the user out only when it means their
// OpenBrain session actually ended (same refresh-then-logout as withAuth). If the session is fine
// (refresh succeeds) but the external service rejects the request again anyway - or returns 403 -
// that's a backend-configuration problem, not a session problem: no logout, just a message a
// non-technical user can read, for existing `toast.error(err.message || "...")` calls.
export const FRIENDLY_UNAUTHORIZED_MESSAGE = "Unauthorized - check Backend Configuration.";
export const FRIENDLY_FORBIDDEN_MESSAGE = "Forbidden - check Backend Configuration.";

export const withAuthLogoutOn401 = (instance) => {
  instance.interceptors.request.use(async (config) => {
    const token = await getValidAccessToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error.config;
      if (error.response?.status === 403) {
        error.message = FRIENDLY_FORBIDDEN_MESSAGE;
        return Promise.reject(error);
      }
      // a 401 right after a refresh is not an expired token - the session is fine, this service
      // just will not accept it, so it is not retried again and does not log the user out
      if (error.response?.status !== 401 || !config || config._retried || !getAccessToken()) {
        if (error.response?.status === 401) error.message = FRIENDLY_UNAUTHORIZED_MESSAGE;
        return Promise.reject(error);
      }
      if (!(await refreshOrLogout())) return Promise.reject(error);
      config._retried = true;
      config.headers.Authorization = `Bearer ${getAccessToken()}`;
      return instance(config);
    }
  );
  return instance;
};
